import { useSetAtom } from "jotai";
import { useState, useRef, ReactNode, ReactElement, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { CanceledError, type AxiosError } from "axios";

import { useSetLocationAndOrg } from "_/hooks/useSetLocationAndOrg";
import { useAddToast } from "_/components/toasts";
import { useLocation } from "_/components/router";

import { isPublicUrl, routeUrls } from "_/routes";
import { nextUrlAtom, useSetTargetOrg } from "_/state";
import { api, isValidUuid } from "_/utils";

// Extract the `?targetOrg` UUID query parameter from the current URL.
// Returns a tuple of [orgId, cleanUrl] where cleanUrl is the current
// URL with the `targetOrg` parameter stripped. orgId is null if the
// parameter is missing or not a valid UUID.
// This is used to handle org switching based on the url, which is used
// in email notifications for new organization membership.
const extractOrgQueryParam = (): [string | null, string] => {
  const url = new URL(window.location.href);
  const orgParam = url.searchParams.get("targetOrg");
  const orgId = isValidUuid(orgParam) ? orgParam : null;

  url.searchParams.delete("targetOrg");
  const cleanUrl = `${url.pathname}${url.search}${url.hash}`;

  return [orgId, cleanUrl];
};

// How long the "server unavailable" toast is shown, and the minimum time
// before another one may be shown.
const SERVER_UNAVAILABLE_TOAST_COOLDOWN_MS = 10_000;

type RequestInterceptorProps = {
  children: ReactNode;
};

// Intercepts HTTP requests and handles specific success and error cases
//
// If the user needs to log in, they will be redirected to a login page, then back to the original
// linked location once logged in.
//
// If a `?targetOrg` query parameter is provided, the current org will be set to this value and the
// parameter will be stripped from the URL, preserving the original destination path.
export const RequestInterceptor = ({
  children,
}: RequestInterceptorProps): ReactElement => {
  const [_, setLocation] = useLocation();
  const [ready, setReady] = useState(false);
  const setNextUrl = useSetAtom(nextUrlAtom);
  const setTargetOrg = useSetTargetOrg();
  const queryClient = useQueryClient();
  const setLocationAndOrg = useSetLocationAndOrg();

  // Toasts and values for server version checks.
  const addToast = useAddToast();
  const serverVersionRef = useRef<string | null>(null);
  const reloadToastShownRef = useRef(false);
  const unavailableToastShownAtRef = useRef(0);
  const sessionAuthenticatedRef = useRef(true);

  useEffect(() => {
    // Check for search param in url to set current org
    const responseInterceptor: Parameters<
      typeof api.interceptors.response.use
    >[0] = (response) => {
      if (
        response.config.method === "post" &&
        response.config.url === "/auth"
      ) {
        sessionAuthenticatedRef.current = true;
      }

      const [orgId, cleanUrl] = extractOrgQueryParam();

      if (orgId) {
        setLocationAndOrg({ orgId, url: cleanUrl });
      }

      // Doesn't seem to be a way to type axios headers, so need a cast.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const version = (response.headers as { "x-aon3d-app-version"?: string })[
        "x-aon3d-app-version"
      ];

      if (version) {
        if (serverVersionRef.current === null) {
          serverVersionRef.current = version;
        } else if (
          version !== serverVersionRef.current &&
          !reloadToastShownRef.current
        ) {
          reloadToastShownRef.current = true;
          addToast({
            title: t`components.interceptor.server-update-toast-title`,
            content: t`components.interceptor.server-update-toast-content`,
            kind: "warning",
            actions: [
              {
                label: t`common.reload`,
                action: () => window.location.reload(),
              },
            ],
          });
        }
      }

      return response;
    };

    const requestInterceptor: Parameters<
      typeof api.interceptors.request.use
    >[0] = (config) => {
      if (
        !sessionAuthenticatedRef.current &&
        !isPublicUrl(window.location.pathname)
      ) {
        return Promise.reject(
          new CanceledError("Session has expired.", undefined, config)
        );
      }

      return config;
    };

    // Clear user and cached data on 401's - this means the user session
    // has ended.
    const errInterceptor: Parameters<
      typeof api.interceptors.response.use
    >[1] = (error: AxiosError) => {
      const unauthenticated = error.response?.status === 401;
      const serverUnavailable = error.response?.status === 502;
      const onPublicPage = isPublicUrl(window.location.pathname);

      // A 502 means the server is unreachable. Many in-flight requests can
      // fail at once, so use a cooldown to avoid stacking duplicate toasts.
      if (serverUnavailable) {
        const now = Date.now();
        const cooldownOver =
          now - unavailableToastShownAtRef.current >
          SERVER_UNAVAILABLE_TOAST_COOLDOWN_MS;

        if (cooldownOver) {
          unavailableToastShownAtRef.current = now;
          addToast({
            title: t`components.interceptor.server-unavailable-toast-title`,
            content: t`components.interceptor.server-unavailable-toast-content`,
            kind: "danger",
            timeout: SERVER_UNAVAILABLE_TOAST_COOLDOWN_MS,
          });
        }
      }

      // If no user and accessing a private page.
      if (unauthenticated && !onPublicPage && sessionAuthenticatedRef.current) {
        sessionAuthenticatedRef.current = false;
        console.warn("Unauthenticated user. Clearing query cache");

        // Search params and hashes are not included in the location returned
        // by the wouter `useLocation` hook, so we construct it manually here.
        const { pathname, search, hash } = window.location;
        const nextUrl = `${pathname}${search}${hash}`;

        queryClient.clear();
        setNextUrl(nextUrl);
        setTargetOrg(extractOrgQueryParam()[0]);

        setLocation(routeUrls.public.login);
      }

      return Promise.reject(error);
    };

    const requestInterceptorId =
      api.interceptors.request.use(requestInterceptor);
    const responseInterceptorId = api.interceptors.response.use(
      responseInterceptor,
      errInterceptor
    );

    setReady(true);

    return () => {
      api.interceptors.request.eject(requestInterceptorId);
      api.interceptors.response.eject(responseInterceptorId);
    };
  }, [
    setNextUrl,
    setLocation,
    queryClient,
    setLocationAndOrg,
    setTargetOrg,
    addToast,
  ]);

  return ready ? <>{children}</> : <></>;
};
